import { AlertTriangle, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';

/**
 * Hard reload that wipes client-side state so the user is guaranteed a fresh
 * boot — not a soft reload that re-uses the broken cached state that put us
 * here. Clears localStorage, sessionStorage, all script-accessible cookies,
 * the HTTP cache (Cache API), and any registered service workers, then
 * forces an unconditional GET via location.replace + cache-buster.
 */
async function hardReload() {
  try { localStorage.clear(); } catch { /* SecurityError in strict mode */ }
  try { sessionStorage.clear(); } catch { /* SecurityError in strict mode */ }
  // Cookies (only non-HttpOnly are visible to JS — HttpOnly auth cookies, if any,
  // survive). Expire each cookie path on each ancestor of the current host.
  document.cookie.split(';').forEach((c) => {
    const name = c.split('=')[0]?.trim();
    if (!name) return;
    const expire = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/`;
    document.cookie = expire;
    const parts = location.hostname.split('.');
    while (parts.length > 1) {
      document.cookie = `${expire}; domain=${parts.join('.')}`;
      parts.shift();
    }
  });
  if ('caches' in window) {
    try {
      const keys = await caches.keys();
      await Promise.all(keys.map((k) => caches.delete(k)));
    } catch { /* ignore */ }
  }
  if ('serviceWorker' in navigator) {
    try {
      const regs = await navigator.serviceWorker.getRegistrations();
      await Promise.all(regs.map((r) => r.unregister()));
    } catch { /* ignore */ }
  }
  // Cache-bust query param + replace so the old URL isn't kept in history.
  const url = new URL(location.href);
  url.searchParams.set('_reset', Date.now().toString());
  url.hash = '';
  location.replace(url.toString());
}

export function ErrorPage() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-[var(--background)]">
      <div className="flex flex-col items-center gap-6 px-4 text-center">
        <AlertTriangle className="size-12 text-destructive" />
        <h1
          role="alert"
          className="text-[28px] font-semibold leading-[1.2] text-foreground"
        >
          Something went wrong
        </h1>
        <p className="max-w-md text-sm text-muted-foreground">
          We hit an unexpected error. Try reloading the page.
        </p>
        <Button autoFocus onClick={() => void hardReload()}>
          <RefreshCw className="size-4" data-icon="inline-start" />
          Reload Page
        </Button>
      </div>
    </div>
  );
}
